import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Users, Check, X } from "lucide-react";
import api from "../lib/api";
import { Avatar } from "../components/Media";
import { SignalPulse } from "../components/SignalPulse";
import { toast } from "sonner";

export default function Hearths() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/hearths"); setData(d.hearths); }
    catch { setData([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (id, accept) => {
    try { await api.post(`/hearths/${id}/respond`, { accept }); toast(accept ? "Joined" : "Declined"); load(); }
    catch { toast.error("Failed"); }
  };

  if (data === null) return <div className="flex justify-center py-24"><SignalPulse size={44} /></div>;
  const active = data.filter((h) => h.status === "active");
  const invited = data.filter((h) => h.status === "invited");

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="hearths-page">
      <h1 className="font-display text-3xl text-white flex items-center gap-2 mb-1"><Lock size={20} className="text-[#14A89A]" /> Hearth Communities</h1>
      <p className="font-body text-sm text-zinc-500 mb-6">Private, encrypted spaces you belong to. Only members can see inside; the Council may view only if something is flagged for safety.</p>

      {invited.length > 0 && (
        <div className="mb-6">
          <h2 className="font-body text-xs uppercase tracking-wide text-amber-300 mb-2">Invitations</h2>
          {invited.map((h) => (
            <div key={h.id} data-testid={`hearth-invite-row-${h.id}`} className="border border-amber-400/30 bg-[#121212] p-4 flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Avatar name={h.owner?.name} src={h.owner?.avatar} iv={h.owner?.avatar_iv} size={34} />
                <div><div className="font-body text-white text-sm">{h.title}</div><div className="font-body text-[11px] text-zinc-500">from {h.owner?.name || "a member"}</div></div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => respond(h.id, true)} data-testid={`accept-${h.id}`} className="flex items-center gap-1 bg-[#14A89A] text-[#0A0A0A] font-semibold px-3 py-1.5 font-body text-xs"><Check size={13} /> Join</button>
                <button onClick={() => respond(h.id, false)} data-testid={`decline-${h.id}`} className="text-zinc-500 hover:text-white px-2"><X size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active.length === 0 && invited.length === 0 && (
        <p className="text-center text-zinc-600 font-body text-sm py-12">You're not in any communities yet. A Beacon creator can invite you, or turn one on from your own Beacon.</p>
      )}

      {active.map((h) => (
        <button key={h.id} onClick={() => nav(`/hearth/${h.id}`)} data-testid={`hearth-row-${h.id}`} className="w-full text-left border border-white/10 bg-[#121212] p-4 flex items-center justify-between mb-2 hover:border-[#14A89A]/40 transition-colors">
          <div className="flex items-center gap-3">
            <Avatar name={h.owner?.name} src={h.owner?.avatar} iv={h.owner?.avatar_iv} size={38} />
            <div>
              <div className="font-body text-white flex items-center gap-2">{h.title}{h.role === "owner" && <span className="text-[10px] text-[#14A89A] uppercase tracking-wide">Yours</span>}</div>
              <div className="font-body text-[11px] text-zinc-500 flex items-center gap-1"><Users size={11} /> {h.member_count} member{h.member_count === 1 ? "" : "s"}</div>
            </div>
          </div>
          <Lock size={14} className="text-zinc-600" />
        </button>
      ))}
    </div>
  );
}
